import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { Roof } from "building-types/roofs/roofSystemTypes";
import { isIndexedRoofPeakDirection } from "building-types/roofs/roofSystemTypes";
import type { GeneratedRoofVolumeLegacy } from "building-types/roofVolumes/roofVolumeTypes";
import type { RoofSettings } from "building-types/settings/roofSettings";
import {
  RoofSide,
  type NeighborRoof,
  type EvaluatedRoofEdgeOverridesTable,
  type EvaluatedRoofSideOverridesTable,
  type RoofSlope,
} from "building-types/settings/roofTypes";
import { RoofWeldMode, GableRoofType } from "building-types/settings/settingsTypesEnum";
import type { Table } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { MULTIGABLE_BSP_EPSILON, neighborRoofNew } from "doc/utils/roofTools";
import { getNodeAABB3, isPointInsideBSP, rayIntersectionBSP } from "geom/bsp";
import type { NodeBSP, RayIntersectionBSP } from "geom/bspTypes";
import { DIST_EPSILON, Line2Side, ONE_MINUS_DIST_EPSILON } from "geom/core";
import { line2IsParallel, line2PerpDistance, line2Side, line2Tangent } from "geom/line2";
import { offsetPolyLine2 } from "geom/offset";
import { planeNewXY } from "geom/plane";
import type { EdgeRef, PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import {
  polyLine2EdgeEnd,
  polyLine2EdgeStart,
  polyLine2EdgeCount,
  polyLine2EdgeLength,
  polyLine2EdgeTangent,
  polyLine2IsAxisAlignedEdge,
  polyLine2Edge,
  polyLine2EdgeEval,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3EvalEdge, polyLine3OnPlane } from "geom/polyLine3";
import { vec2Dist, vec2Dot } from "geom/vec2";
import { getSubRoofSlopes, buildSlopes, getBaseToPeak, getExtensions, getPeakExtension } from "./roofSlopes";

interface TouchesBuildingOptions {
  readonly node: NodeBSP | null;
  readonly leftRail: PolyLine2;
  readonly rightRail: PolyLine2;
  readonly peakRail: PolyLine2;
  readonly baseHeight: number;
  readonly peakHeight: number;
  readonly leftOverhang: number;
  readonly rightOverhang: number;
  readonly endSide: boolean;
  readonly peakOffsetZ?: number;
}

export function touchesBuilding({
  node,
  leftRail,
  rightRail,
  peakRail,
  baseHeight,
  peakHeight,
  leftOverhang,
  rightOverhang,
  endSide,
  peakOffsetZ = 0,
}: TouchesBuildingOptions): boolean {
  const offsetZ = MULTIGABLE_BSP_EPSILON * 2;
  const basePlane = planeNewXY(0, 0, baseHeight + offsetZ);
  const peakPlane = planeNewXY(0, 0, Math.max(baseHeight, peakHeight + peakOffsetZ + offsetZ));
  const leftRail3 = polyLine3OnPlane(offsetPolyLine2(leftRail, leftOverhang + 1.0), basePlane);
  const peakRail3 = polyLine3OnPlane(peakRail, peakPlane);
  const rightRail3 = polyLine3OnPlane(offsetPolyLine2(rightRail, -rightOverhang - 1.0), basePlane);

  const startLeftIndex = endSide ? leftRail3.length - 3 : 0;
  const startPeakIndex = endSide ? peakRail3.length - 3 : 0;
  const startRightIndex = endSide ? rightRail3.length - 3 : 0;

  const isLeftTouching = isPointInsideBSP(
    node,
    leftRail3[startLeftIndex],
    leftRail3[startLeftIndex + 1],
    leftRail3[startLeftIndex + 2],
    false,
    MULTIGABLE_BSP_EPSILON,
  );
  const isPeakTouching = isPointInsideBSP(
    node,
    peakRail3[startPeakIndex],
    peakRail3[startPeakIndex + 1],
    peakRail3[startPeakIndex + 2],
    false,
    MULTIGABLE_BSP_EPSILON,
  );
  const isRightTouching = isPointInsideBSP(
    node,
    rightRail3[startRightIndex],
    rightRail3[startRightIndex + 1],
    rightRail3[startRightIndex + 2],
    false,
    MULTIGABLE_BSP_EPSILON,
  );

  return isLeftTouching && isRightTouching && isPeakTouching;
}

export function tryGetNeighborRoofSlope(
  subRoofPerimeter: PolyLine2,
  subGablePolygonIndexToRoofId: Table<string>,
  insertedRoofVolumes: Table<GeneratedRoofVolumeLegacy>,
  neighbors: EdgeRef[][],
  edgeIndex: number,
  baseHeight: number,
  mirror: boolean,
): NeighborRoof {
  const edgeNeighbors = neighbors[edgeIndex];

  const [x0, y0] = polyLine2EdgeStart(subRoofPerimeter, edgeIndex);
  const [x1, y1] = polyLine2EdgeEnd(subRoofPerimeter, edgeIndex);

  for (let i = 0, l = edgeNeighbors.length; i < l; i++) {
    const finalI = mirror ? l - i - 1 : i;
    const edgeNeighbor = edgeNeighbors[finalI];
    if (edgeNeighbor === null) {
      continue;
    }

    const subRoofId = subGablePolygonIndexToRoofId[edgeNeighbor.index];
    if (subRoofId === undefined) {
      continue;
    }

    const roofVolume = insertedRoofVolumes[subRoofId];
    if (!roofVolume) {
      continue;
    }

    const [x3, y3] = polyLine3EvalEdge(roofVolume.rails.peakRail, 0, 0.5, true);
    if (line2Side(x0, y0, x1, y1, x3, y3) !== Line2Side.RIGHT) {
      continue;
    }

    return tryGetNeighborRoof(roofVolume, baseHeight, edgeNeighbor.edgeIndex);
  }

  return neighborRoofNew(baseHeight);
}

export function tryGetNeighborRoof(
  roofVolume: GeneratedRoofVolumeLegacy,
  baseHeight: number,
  neighborEdgeIndex: number,
): NeighborRoof {
  if (!roofVolume) {
    return neighborRoofNew(baseHeight);
  }

  const {
    truncatedSlopes,
    slopes,
    oppSlopes,
    side,
    startHeight,
    heightOffset,
    sideOverhang,
    startOverhang,
    endOverhang,
  } = roofVolume.edges[neighborEdgeIndex];

  return neighborRoofNew(
    startHeight - heightOffset,
    heightOffset,
    sideOverhang,
    startOverhang,
    endOverhang,
    0,
    0,
    truncatedSlopes,
    oppSlopes,
    roofVolume.id,
    side,
    truncatedSlopes.length > slopes.length,
  );
}

interface GetNeighborRoofsOptions {
  readonly roofSystemId: string;
  readonly polygonId: number;
  readonly subRoof: Roof;
  readonly boundingPerimeter: PolyLine2;
  readonly exactPerimeter: PolyLine2;
  readonly baseHeight: number;
  readonly eaveOverhang: number;
  readonly baseEaveOverhang: number;
  readonly clipContext: MultiGableRoofClipContext;
  readonly planarGraph: PolygonPlanarGraph;
  readonly subGablePolygonIndexToRoofId: Table<string>;
  readonly insertedRoofVolumes: Table<GeneratedRoofVolumeLegacy>;
  readonly roofSettings: RoofSettings;
  readonly roofSideOverrides: EvaluatedRoofSideOverridesTable;
  readonly roofEdgeOverrides: EvaluatedRoofEdgeOverridesTable;
  readonly mirror: boolean;
}

export function getNeighborRoofs({
  roofSystemId,
  polygonId,
  subRoof,
  boundingPerimeter,
  exactPerimeter,
  baseHeight,
  eaveOverhang,
  baseEaveOverhang,
  clipContext,
  planarGraph,
  subGablePolygonIndexToRoofId,
  insertedRoofVolumes,
  roofSettings,
  roofSideOverrides,
  roofEdgeOverrides,
  mirror,
}: GetNeighborRoofsOptions): NeighborRoof[] {
  let neighborRoofs: NeighborRoof[] = [];

  // fill the neighbor roofs with empty values
  neighborRoofs = new Array(polyLine2EdgeCount(boundingPerimeter)).fill(neighborRoofNew(baseHeight));

  const { parentNeighbors = [] } = subRoof;
  const { peakDirection, weldMode = RoofWeldMode.AUTO, roofType = GableRoofType.DEFAULT } = roofSettings;

  // if it's a regular roof
  if (polygonId >= 0 && (peakDirection === undefined || !isIndexedRoofPeakDirection[peakDirection])) {
    // get the neighbor roofs from the planar graph
    const neighbors = planarGraph.polygonNeighbors(polygonId);
    for (let j = 0, lj = neighbors.length; j < lj; ++j) {
      if (weldMode === RoofWeldMode.MANUAL) {
        let bestRoofVolume: GeneratedRoofVolumeLegacy | undefined;
        let bestRoofVolumeEdge: number = -1;

        const [tx, ty] = polyLine2EdgeTangent(boundingPerimeter, j);
        const [x0, y0] = polyLine2EdgeStart(boundingPerimeter, j);
        const [x1, y1] = polyLine2EdgeEnd(boundingPerimeter, j);

        let biggestDist = Number.NEGATIVE_INFINITY;
        for (const parentNeighbor of parentNeighbors) {
          for (const id in insertedRoofVolumes) {
            const otherRoofVolume = insertedRoofVolumes[id];
            if (!otherRoofVolume || otherRoofVolume.id !== parentNeighbor) {
              continue;
            }

            const { edges: otherEdges, rails: otherRails } = otherRoofVolume;
            const { peakRail: otherPeakRail } = otherRails;

            const [x3, y3] = polyLine3EvalEdge(otherPeakRail, 0, 0.5, true);
            if (line2Side(x0, y0, x1, y1, x3, y3) !== Line2Side.RIGHT) {
              continue;
            }

            for (let k = 0, lk = otherEdges.length; k < lk; k++) {
              const { edge: otherEdge } = otherEdges[k];
              const [otherTx, otherTy] = polyLine2EdgeTangent(otherEdge, 0);

              // exclude perpendicular edges
              if (Math.abs(vec2Dot(tx, ty, otherTx, otherTy)) < DIST_EPSILON) {
                continue;
              }

              const dist = line2PerpDistance(x0, y0, x1, y1, x3, y3);

              if (dist > biggestDist) {
                bestRoofVolume = otherRoofVolume;
                bestRoofVolumeEdge = k;
                biggestDist = dist;
              }
            }
          }
        }

        if (!bestRoofVolume) {
          continue;
        }

        const { slopes, oppSlopes, side, startHeight, heightOffset, sideOverhang, startOverhang, endOverhang } =
          bestRoofVolume.edges[bestRoofVolumeEdge];

        neighborRoofs[j] = neighborRoofNew(
          startHeight - heightOffset,
          heightOffset,
          sideOverhang,
          startOverhang,
          endOverhang,
          0,
          0,
          slopes,
          oppSlopes,
          bestRoofVolume.id,
          side,
        );
      } else {
        neighborRoofs[j] = tryGetNeighborRoofSlope(
          boundingPerimeter,
          subGablePolygonIndexToRoofId,
          insertedRoofVolumes,
          neighbors.map((edgeRefs) => edgeRefs.filter((edgeRef) => edgeRef !== null)) as EdgeRef[][],
          j,
          baseHeight,
          mirror,
        );
      }
    }
  }

  if ((polygonId < 0 || roofType !== GableRoofType.DEFAULT) && weldMode === RoofWeldMode.AUTO) {
    const { roofsBSP } = clipContext;
    const aabb3 = getNodeAABB3(roofsBSP);

    // iterate the 2 main directions
    for (let startEdge = 0; startEdge < 2; startEdge++) {
      // only for axis aligned edges
      if (!polyLine2IsAxisAlignedEdge(boundingPerimeter, startEdge)) {
        continue;
      }

      // get the slopes for the current direction
      const slopes = getSubRoofSlopes({
        exactPerimeter,
        boundingPerimeter,
        startEdge,
        roofSettings,
        roofSideOverrides,
        roofEdgeOverrides,
      });

      // generate the roof information for the current direction
      const endEdge = (startEdge + 2) % 4;
      const span = polyLine2EdgeLength(boundingPerimeter, startEdge) + 2.0 * eaveOverhang;
      const finalSlopes = buildSlopes({ slopes, halfSpan: span * 0.5, roofOverhang: baseEaveOverhang });
      const baseToPeak = getBaseToPeak(finalSlopes);
      const peakElevation = baseHeight + baseToPeak;
      const startNeighborRoofVolume = insertedRoofVolumes[neighborRoofs[startEdge].roofId];
      const endNeighborRoofVolume = insertedRoofVolumes[neighborRoofs[endEdge].roofId];

      // look for the neighbor on the start edge
      if (!startNeighborRoofVolume) {
        const startNeighbor = getRaytracedNeighborRoof({
          boundingPerimeter,
          edgeIndex: endEdge,
          baseHeight,
          peakElevation,
          eaveOverhang,
          baseEaveOverhang,
          roofType,
          slopes: finalSlopes,
          roofsBSP,
          roofSystemId,
          insertedRoofVolumes,
          maxZ: aabb3.maxZ,
        });
        if (startNeighbor) {
          neighborRoofs[startEdge] = startNeighbor;
        }
      }

      // look for the neighbor on the end edge
      if (!endNeighborRoofVolume) {
        const endNeighbor = getRaytracedNeighborRoof({
          boundingPerimeter,
          edgeIndex: startEdge,
          baseHeight,
          peakElevation,
          eaveOverhang,
          baseEaveOverhang,
          roofType,
          slopes: finalSlopes,
          roofsBSP,
          roofSystemId,
          insertedRoofVolumes,
          maxZ: aabb3.maxZ,
        });
        if (endNeighbor) {
          neighborRoofs[endEdge] = endNeighbor;
        }
      }
    }
  }

  return neighborRoofs;
}

interface GetRaytracedNeighborRoofOptions {
  boundingPerimeter: PolyLine2;
  edgeIndex: number;
  baseHeight: number;
  peakElevation: number;
  eaveOverhang: number;
  baseEaveOverhang: number;
  slopes: RoofSlope[];
  roofType: GableRoofType;
  roofsBSP: NodeBSP | null;
  roofSystemId: string;
  insertedRoofVolumes: Table<GeneratedRoofVolumeLegacy>;
  maxZ: number;
}

function getRaytracedNeighborRoof({
  boundingPerimeter,
  edgeIndex,
  baseHeight,
  peakElevation,
  eaveOverhang,
  baseEaveOverhang,
  roofType,
  slopes,
  roofsBSP,
  roofSystemId,
  insertedRoofVolumes,
  maxZ,
}: GetRaytracedNeighborRoofOptions): NeighborRoof | null {
  const startZ = maxZ + 1;
  const endZ = 0;

  const getSpan = (edgeIndex: number): number => {
    return polyLine2EdgeLength(boundingPerimeter, edgeIndex) + 2.0 * eaveOverhang;
  };

  const isValidRoofIntersection = (intersection: RayIntersectionBSP | null): boolean => {
    if (!intersection || !intersection.entryPoint || !intersection.entryPoint.userData) {
      return false;
    }

    const subRoofId = intersection.entryPoint.userData as string;

    const subRoof = insertedRoofVolumes[subRoofId];

    if (!subRoof || subRoof.roofSystemId !== roofSystemId) {
      return false;
    }

    return intersection.entryPoint.z >= subRoof.baseHeight - DIST_EPSILON;
  };

  // get the peak/center real using this edge as the start edge
  const edge = polyLine2Edge(boundingPerimeter, edgeIndex);
  const oppEdge = polyLine2Edge(boundingPerimeter, (edgeIndex + 2) % 4);
  const [startX, startY] = polyLine2EdgeEval(edge, 0, 0.5, true);
  const [endX, endY] = polyLine2EdgeEval(oppEdge, 0, 0.5, true);
  const [dirX, dirY] = line2Tangent(startX, startY, endX, endY);
  const peakLength = vec2Dist(startX, startY, endX, endY);
  const halfSpan = getSpan(edgeIndex) * 0.5;

  const rayLength = 10000;
  const isShedRoof = roofType !== GableRoofType.DEFAULT;
  const allowPopUp = !isShedRoof;

  let rayElevation = peakElevation + 2 * MULTIGABLE_BSP_EPSILON;
  let peakExtensionOffset = peakLength;

  // trace horizontal ray
  let intersection = rayIntersectionBSP({
    node: roofsBSP,
    coplanarsToFront: false,
    x1: startX,
    y1: startY,
    z1: rayElevation,
    x2: startX + dirX * rayLength,
    y2: startY + dirY * rayLength,
    z2: rayElevation,
  });

  const offsetFactors = [0.5, ONE_MINUS_DIST_EPSILON, -0.5, -ONE_MINUS_DIST_EPSILON];

  // if not found trace horizontal ray with offsets for shed roofs
  if (!isValidRoofIntersection(intersection) && isShedRoof) {
    peakExtensionOffset = 0;
    for (let i = 0, l = offsetFactors.length; i < l; ++i) {
      const offset = halfSpan * offsetFactors[i];
      intersection = rayIntersectionBSP({
        node: roofsBSP,
        coplanarsToFront: false,
        x1: endX - dirY * offset,
        y1: endY + dirX * offset,
        z1: rayElevation,
        x2: endX - dirY * offset + dirX * rayLength,
        y2: endY + dirX * offset + dirY * rayLength,
        z2: rayElevation,
      });

      if (isValidRoofIntersection(intersection)) {
        break;
      }
    }
  }

  // look for pop up
  if (!isValidRoofIntersection(intersection) && allowPopUp) {
    peakExtensionOffset = peakLength;
    // trace a vertical ray
    const verticalIntersection = rayIntersectionBSP({
      node: roofsBSP,
      coplanarsToFront: false,
      x1: startX + dirX,
      y1: startY + dirY,
      z1: startZ,
      x2: startX + dirX,
      y2: startY + dirY,
      z2: endZ,
    });

    if (isValidRoofIntersection(verticalIntersection)) {
      assertIsNotUndefined(verticalIntersection?.entryPoint);

      const hostSubRoofId = verticalIntersection.entryPoint.userData as string;
      const neighborSubRoof = insertedRoofVolumes[hostSubRoofId];

      if (neighborSubRoof) {
        // launch the horizontal rail again with a new ray elevation
        rayElevation = neighborSubRoof.peakHeight - 1;
        intersection = rayIntersectionBSP({
          node: roofsBSP,
          coplanarsToFront: false,
          x1: startX,
          y1: startY,
          z1: rayElevation,
          x2: startX + dirX * rayLength,
          y2: startY + dirY * rayLength,
          z2: rayElevation,
        });

        if (!isValidRoofIntersection(intersection)) {
          rayElevation = peakElevation - 1;
          intersection = rayIntersectionBSP({
            node: roofsBSP,
            coplanarsToFront: false,
            x1: startX,
            y1: startY,
            z1: rayElevation,
            x2: startX + dirX * rayLength,
            y2: startY + dirY * rayLength,
            z2: rayElevation,
          });
        }
      }
    }
  }

  // return if no roof is found or the roof is from another roof system
  if (!isValidRoofIntersection(intersection)) {
    return null;
  }

  assertIsNotUndefined(intersection?.entryPoint);

  // get the potential neighbor roof information
  const hostSubRoofId = intersection.entryPoint.userData as string;
  const neighborRoofVolume = insertedRoofVolumes[hostSubRoofId];

  if (!neighborRoofVolume) {
    return null;
  }

  const {
    baseHeight: neighborBaseHeight,
    leftSpan: neighborLeftSpan,
    rightSpan: neighborRightSpan,
    leftSideSlopes: neighborLeftSlopes,
    rightSideSlopes: neighborRightSlopes,
    heelHeightOffsets: neighborHeelHeightOffsets,
    rectangularOverhangs: neighborRectangularOverhangs,
    rails: neighborRails,
  } = neighborRoofVolume;

  const {
    leftOverhang: neighborLeftEaveOverhang,
    rightOverhang: neighborRightEaveOverhang,
    startOverhang: neighborStartRoofOverhang,
    endOverhang: neighborEndRoofOverhang,
  } = neighborRectangularOverhangs;

  const { leftRail: neigborLeftRail, rightRail: neighborRightRail, peakRail: neighborPeak } = neighborRails;

  const p0x = neighborPeak[0];
  const p0y = neighborPeak[1];
  const p1x = neighborPeak[3];
  const p1y = neighborPeak[4];
  const intersectionSide = line2Side(p0x, p0y, p1x, p1y, intersection.entryPoint.x, intersection.entryPoint.y);
  const side = line2Side(p0x, p0y, p1x, p1y, startX, startY);

  if (side !== intersectionSide) {
    return null;
  }

  const neighborSlopes = intersectionSide === Line2Side.LEFT ? neighborLeftSlopes : neighborRightSlopes;
  const neighborOppSlopes = intersectionSide === Line2Side.LEFT ? neighborRightSlopes : neighborLeftSlopes;
  const neighborHeightOffset =
    intersectionSide === Line2Side.LEFT ? neighborHeelHeightOffsets.leftOffset : neighborHeelHeightOffsets.rightOffset;

  const neighborSideSpan = intersectionSide === Line2Side.LEFT ? neighborLeftSpan : neighborRightSpan;
  const neighborEaveOverhang =
    intersectionSide === Line2Side.LEFT ? neighborLeftEaveOverhang : neighborRightEaveOverhang;
  const distToPeakRail = line2PerpDistance(p0x, p0y, p1x, p1y, startX, startY);
  const depth = polyLine2EdgeLength(boundingPerimeter, edgeIndex + 1);

  if (distToPeakRail - depth > neighborSideSpan - neighborEaveOverhang + DIST_EPSILON) {
    return null;
  }

  // prepare some data for the current roof and edge
  const relativeHeight = baseHeight - neighborRoofVolume.baseHeight + neighborHeightOffset;
  const extensions = getExtensions(
    buildSlopes({
      slopes,
      startRelativeHeight: relativeHeight,
      halfSpan,
      roofOverhang: baseEaveOverhang,
    }),
    neighborSlopes,
    allowPopUp,
    0,
    relativeHeight,
  );

  const currPeakExtension = intersection.entryPoint.distToStart - peakExtensionOffset;
  const maxPeakExtensions = getPeakExtension(extensions) + 1;

  if (currPeakExtension > maxPeakExtensions) {
    return null;
  }

  // get the current roof side on the host roof
  let relativeDist = neighborSideSpan - distToPeakRail - neighborEaveOverhang;
  if (line2IsParallel(p0x, p0y, p1x, p1y, 0, 0, dirX, dirY)) {
    const relativeDist1 = line2PerpDistance(
      neigborLeftRail[0],
      neigborLeftRail[1],
      neighborRightRail[0],
      neighborRightRail[1],
      startX,
      startY,
    );

    const relativeDist2 = line2PerpDistance(
      neigborLeftRail[3],
      neigborLeftRail[4],
      neighborRightRail[3],
      neighborRightRail[4],
      startX,
      startY,
    );

    relativeDist = Math.min(relativeDist1, relativeDist2) - eaveOverhang;
  }

  // return the neighbor roof information
  return neighborRoofNew(
    neighborBaseHeight,
    neighborHeightOffset,
    neighborEaveOverhang,
    neighborStartRoofOverhang,
    neighborEndRoofOverhang,
    relativeDist + depth,
    depth,
    neighborSlopes,
    neighborOppSlopes,
    neighborRoofVolume.id,
    intersectionSide === Line2Side.LEFT ? RoofSide.LEFT : RoofSide.RIGHT,
  );
}
